import React, {useEffect, useMemo, useState} from 'react';
import {apiService} from '../services/api';
import {useAdmin} from './AdminContext';
import {formatDate, formatPrice} from './adminUtils';

interface AdminProductSummary {
  id: string;
  name: string;
  tokens: number;
  price: number;
  descriptionEn: string;
  descriptionSr: string;
  popular: boolean;
  active: boolean;
  sortOrder: number;
  createdAt?: string | null;
}

const emptyProduct = {
  name: '',
  tokens: 0,
  price: 0,
  descriptionEn: '',
  descriptionSr: '',
  popular: false,
  active: true,
  sortOrder: 0,
};

const AdminProductsPage: React.FC = () => {
  const {token} = useAdmin();
  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({...emptyProduct});

  const editingProduct = useMemo(
    () => products.find((product) => product.id === editingId) || null,
    [products, editingId]
  );

  const loadProducts = async () => {
    if (!token) return;
    setLoading(true);
    setDataError(null);
    try {
      const data = await apiService.adminGetProducts(token);
      setProducts(data);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje proizvoda.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [token]);

  const handleEdit = (product: AdminProductSummary) => {
    setEditingId(product.id);
    setForm({
      name: product.name,
      tokens: product.tokens,
      price: product.price,
      descriptionEn: product.descriptionEn,
      descriptionSr: product.descriptionSr,
      popular: product.popular,
      active: product.active,
      sortOrder: product.sortOrder,
    });
  };

  const handleReset = () => {
    setEditingId(null);
    setForm({...emptyProduct});
  };

  const handleSave = async () => {
    if (!token) return;
    setDataError(null);
    try {
      if (!editingId) {
        setDataError('Izaberite paket koji zelite da izmenite.');
        return;
      }
      await apiService.adminUpdateProduct(token, editingId, form);
      await loadProducts();
      handleReset();
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno cuvanje proizvoda.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-neutral-900">Token paketi</h2>
        <p className="mt-1 text-neutral-500">Upravljanje ponudom i cenama.</p>
      </div>

      {dataError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {dataError}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-neutral-900">Proizvodi</h3>
            <button className="btn-outline" type="button" onClick={loadProducts} disabled={loading}>
              {loading ? 'Ucitavam...' : 'Osvezi'}
            </button>
          </div>
          <div className="space-y-3">
            {products.map((product) => (
              <div key={product.id} className="rounded-xl border border-neutral-200 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-neutral-900">{product.name}</p>
                    <p className="text-xs text-neutral-500">{formatDate(product.createdAt)}</p>
                  </div>
                  <div className="text-sm text-neutral-600">
                    {formatPrice(product.price, 'RSD')} • {product.tokens} tokena
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-sm text-neutral-500">
                  <span>{product.active ? 'Aktivan' : 'Neaktivan'}</span>
                  <span>{product.popular ? 'Popularno' : 'Standard'}</span>
                  <span>Sort: {product.sortOrder}</span>
                </div>
                <button className="btn-outline mt-3 text-xs" type="button" onClick={() => handleEdit(product)}>
                  Izmeni
                </button>
              </div>
            ))}
            {!products.length && !loading && <p className="text-sm text-neutral-500">Nema proizvoda.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
          <h3 className="text-xl font-semibold text-neutral-900">Izmeni paket</h3>
          {!editingProduct && (
            <p className="mt-1 text-sm text-neutral-500">Izaberite paket sa liste da biste ga izmenili.</p>
          )}
          <div className="mt-4 space-y-3">
            <input
              className="input-field"
              placeholder="Naziv paketa"
              value={form.name}
              onChange={(event) => setForm({...form, name: event.target.value})}
              disabled={!editingProduct}
            />
            <div className="grid gap-3 md:grid-cols-2">
              <input
                className="input-field"
                type="number"
                placeholder="Tokeni"
                value={form.tokens}
                onChange={(event) => setForm({...form, tokens: Number(event.target.value)})}
                disabled={!editingProduct}
              />
              <input
                className="input-field"
                type="number"
                placeholder="Cena"
                value={form.price}
                onChange={(event) => setForm({...form, price: Number(event.target.value)})}
                disabled={!editingProduct}
              />
            </div>
            <textarea
              className="input-field min-h-[80px]"
              placeholder="Opis (EN)"
              value={form.descriptionEn}
              onChange={(event) => setForm({...form, descriptionEn: event.target.value})}
              disabled={!editingProduct}
            />
            <textarea
              className="input-field min-h-[80px]"
              placeholder="Opis (SR)"
              value={form.descriptionSr}
              onChange={(event) => setForm({...form, descriptionSr: event.target.value})}
              disabled={!editingProduct}
            />
            <div className="grid gap-3 md:grid-cols-3">
              <input
                className="input-field"
                type="number"
                placeholder="Sort"
                value={form.sortOrder}
                onChange={(event) => setForm({...form, sortOrder: Number(event.target.value)})}
                disabled={!editingProduct}
              />
              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(event) => setForm({...form, active: event.target.checked})}
                  disabled={!editingProduct}
                />
                Aktivan
              </label>
              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input
                  type="checkbox"
                  checked={form.popular}
                  onChange={(event) => setForm({...form, popular: event.target.checked})}
                  disabled={!editingProduct}
                />
                Popularno
              </label>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className="btn-primary" type="button" onClick={handleSave} disabled={!editingProduct}>
              Sacuvaj izmene
            </button>
            {editingProduct && (
              <button className="btn-outline" type="button" onClick={handleReset}>
                Odustani
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductsPage;
